import { useState } from "react";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import {
  Stethoscope,
  ClipboardList,
  FileText,
  Sparkles,
  Users,
  Smartphone,
} from "lucide-react";

const tabs = [
  {
    id: "encounters",
    label: "Encounter Management",
    icon: Stethoscope,
    screenshot: "/screenshots/encounter-detail.png",
    alt: "Encounter detail view showing transcript, clinical context, vitals, and AI-generated notes",
    caption:
      "View complete encounter details including AI-generated transcripts, clinical context, vitals, and assessment — all organized in a tabbed interface.",
  },
  {
    id: "new-encounter",
    label: "New Encounter",
    icon: ClipboardList,
    screenshot: "/screenshots/encounter-new.png",
    alt: "New encounter form with patient info, clinical context, vitals, audio recording, and additional context fields",
    caption:
      "Start a new encounter with structured clinical forms, real-time audio recording, and SOAP-format documentation — all in one seamless workflow.",
  },
  {
    id: "documents",
    label: "Document Viewer",
    icon: FileText,
    screenshot: "/screenshots/documents-viewer.png",
    alt: "Documents list with slide-over viewer showing a generated prescription summary",
    caption:
      "Browse generated documents, view prescriptions and summaries in the slide-over viewer, and export to PDF or print with a single click.",
  },
  {
    id: "ai-generate",
    label: "AI Document Generation",
    icon: Sparkles,
    screenshot: "/screenshots/generate-document.png",
    alt: "Generate Document dialog allowing AI-powered document creation from encounter transcripts",
    caption:
      "Generate clinical documents from any encounter with one click. Choose from prescription summaries, referral letters, patient summaries, and more.",
  },
  {
    id: "patients",
    label: "Patient Registry",
    icon: Users,
    screenshot: "/screenshots/patients-list.png",
    alt: "Patient registry showing MRN, demographics, EHR source, encounter count, and last visit date",
    caption:
      "Manage your patient registry with MRN tracking, EHR source identification (Epic, Cerner, Manual), encounter history, and quick search.",
  },
  {
    id: "mobile",
    label: "Mobile Ready",
    icon: Smartphone,
    screenshot: "/screenshots/mobile-encounter-detail.png",
    alt: "ConsultNow mobile view showing encounter detail on a phone",
    caption:
      "Access ConsultNow on any device. The fully responsive interface adapts to phones and tablets for bedside documentation and on-the-go access.",
    isMobile: true,
  },
];

export function Showcase() {
  const [activeTab, setActiveTab] = useState("encounters");
  const activeItem = tabs.find((t) => t.id === activeTab)!;

  return (
    <section
      id="showcase"
      className="py-20 lg:py-28 bg-white scroll-mt-16"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <p className="text-sm font-semibold uppercase tracking-widest text-brand-red mb-3">
            Product Tour
          </p>
          <h2 className="text-3xl sm:text-4xl font-bold text-brand-navy mb-4">
            See ConsultNow in Action
          </h2>
          <p className="text-lg text-slate-500 leading-relaxed">
            Explore the interface your care team will use every day — from
            encounters and transcription to AI-powered document generation.
          </p>
        </div>

        <div className="flex flex-wrap justify-center gap-2 sm:gap-3 mb-10">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                "flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold transition-all duration-200",
                activeTab === tab.id
                  ? "bg-brand-navy text-white shadow-lg shadow-brand-navy/20"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200 hover:text-brand-navy"
              )}
            >
              <tab.icon className="h-4 w-4" />
              <span className="hidden sm:inline">{tab.label}</span>
            </button>
          ))}
        </div>

        <div className="relative">
          <div className="absolute inset-0 -m-4 bg-gradient-to-br from-brand-navy/5 via-transparent to-brand-red/5 rounded-3xl" />

          <div className="relative flex flex-col items-center">
            <div
              className={cn(
                "rounded-2xl overflow-hidden shadow-2xl shadow-slate-300/50 border border-slate-200",
                activeItem.isMobile ? "max-w-xs" : "w-full max-w-5xl"
              )}
            >
              <img
                src={activeItem.screenshot}
                alt={activeItem.alt}
                className="w-full h-auto"
              />
            </div>

            <div className="mt-8 max-w-2xl text-center">
              <Badge
                variant="secondary"
                className="mb-3 bg-brand-navy/5 text-brand-navy border-brand-navy/10"
              >
                <activeItem.icon className="h-3 w-3 mr-1.5" />
                {activeItem.label}
              </Badge>
              <p className="text-slate-600 leading-relaxed">
                {activeItem.caption}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
